/**
 * H16 ดันเล็ก: one small OPEN dungeon camp per town/city/plains (no instance). Minions, a sub-boss and a boss stand in
 * an area of the zone; clearing them all pays that camp's DAILY reward once per player per game day, then the camp
 * respawns after `respawn` seconds (systems/smallDungeons.ts). Monster defs are made here from a theme's stats and are
 * folded into monsters.ts (respawn null: the camp, not the monster, owns the respawn). Dummy numbers; balance later.
 */
import type { PassReward } from './pass';
import type { MonsterDef } from './monsters';
import type { ZoneId } from './zones';

interface Theme {
  readonly key: string;
  readonly sprite: string;
  readonly subSprite: string;
  readonly bossSprite: string;
  readonly attack: string;
  readonly break?: string;
  readonly canBlock: boolean;
  readonly hp: number;
  readonly atk: number;
  readonly def: number;
  readonly speed: number;
  readonly level: number;
  /** Names: minion, sub-boss, boss. */
  readonly names: readonly [string, string, string];
}

const THEMES: Readonly<Record<string, Theme>> = {
  bandit: { key: 'bandit', sprite: 'bandit', subSprite: 'maskedMan', bossSprite: 'maskedChief', attack: 'bandit-slash', break: 'bandit-crush', canBlock: true, hp: 150, atk: 14, def: 10, speed: 2.2, level: 4, names: ['สมุนโจร', 'รองหัวหน้าโจร', 'หัวหน้าโจร'] },
  pirate: { key: 'pirate', sprite: 'maskedMan', subSprite: 'maskedMan', bossSprite: 'maskedChief', attack: 'bandit-slash', break: 'bandit-crush', canBlock: true, hp: 170, atk: 15, def: 10, speed: 2.3, level: 5, names: ['ลูกเรือโจรสลัด', 'นายท้ายเรือโจร', 'กัปตันโจรสลัด'] },
  scorpion: { key: 'scorpion', sprite: 'scorpion', subSprite: 'scorpion', bossSprite: 'scorpion', attack: 'scorpion-pincer', break: 'scorpion-sting', canBlock: false, hp: 170, atk: 14, def: 14, speed: 1.9, level: 5, names: ['แมงป่องทราย', 'แมงป่องเกราะแดง', 'ราชินีแมงป่อง'] },
  wolf: { key: 'wolf', sprite: 'snowwolf', subSprite: 'snowwolf', bossSprite: 'snowwolf', attack: 'wolf-bite', canBlock: false, hp: 260, atk: 22, def: 12, speed: 3.0, level: 9, names: ['หมาป่าหิมะ', 'หมาป่าเขี้ยวเงิน', 'จ่าฝูงหมาป่า'] },
  bear: { key: 'bear', sprite: 'boar', subSprite: 'bear', bossSprite: 'bear', attack: 'bear-swipe', break: 'bear-maul', canBlock: false, hp: 300, atk: 18, def: 12, speed: 2.0, level: 7, names: ['หมูป่าดุ', 'หมีดำหนุ่ม', 'หมีเฒ่าเจ้าถ้ำ'] },
};

/** Sub-boss / boss multipliers on the minion's hp, attack. */
const SUB = { hp: 3, atk: 1.25, scale: 1.2 } as const;
const BOSS = { hp: 8, atk: 1.6, scale: 1.45 } as const;

const mk = (id: string, name: string, t: Theme, k: { hp: number; atk: number; scale: number }, tier: number, sprite: string): MonsterDef => ({
  id,
  name,
  level: t.level + tier * 2,
  hp: Math.round(t.hp * k.hp),
  atk: Math.round(t.atk * k.atk),
  def: t.def + tier * 3,
  speed: t.speed,
  aggro: 6,
  leash: 16,
  decisionRate: 1.2 - tier * 0.1,
  attack: t.attack,
  break: t.break,
  canBlock: t.canBlock || tier > 0,
  reward: { cultivation: 14 + tier * 40, gold: [6 + tier * 20, 14 + tier * 40] },
  respawn: null,
  sprite,
  scale: k.scale,
});

export interface SmallDungeon {
  readonly id: string;
  readonly zone: ZoneId;
  readonly name: string;
  readonly theme: string;
  readonly minion: string;
  readonly sub: string;
  readonly boss: string;
  readonly minions: number;
  /** Centre (tiles) and radius of the camp's open area. */
  readonly at: { readonly x: number; readonly y: number };
  readonly radius: number;
  /** Seconds until a cleared camp respawns. */
  readonly respawn: number;
  /** The daily reward, once per player per game day. */
  readonly reward: PassReward;
}

const camp = (id: string, zone: ZoneId, name: string, theme: string, at: { x: number; y: number }, radius: number, reward: PassReward, minions = 5, respawn = 600): SmallDungeon => ({
  id, zone, name, theme, minion: `sd-${id}-m`, sub: `sd-${id}-s`, boss: `sd-${id}-b`, minions, at, radius, respawn, reward,
});

export const SMALL_DUNGEONS: readonly SmallDungeon[] = [
  camp('town-b', 'town-b', 'ค่ายโจรเชิงเขา', 'bandit', { x: 20, y: 76 }, 9, { gold: 120, cultivation: 80, jade: 5 }),
  camp('town-c', 'town-c', 'ถ้ำหมีเฒ่า', 'bear', { x: 20, y: 76 }, 9, { gold: 130, cultivation: 90, jade: 5 }),
  camp('town-d', 'town-d', 'ลานหมาป่าชายป่า', 'wolf', { x: 20, y: 76 }, 9, { gold: 150, cultivation: 110, jade: 5 }),
  camp('city-trade', 'city-trade', 'ค่ายโจรริมทางค้า', 'bandit', { x: 20, y: 76 }, 9, { gold: 180, cultivation: 120, jade: 6 }),
  camp('city-sea', 'city-sea', 'อ่าวโจรสลัดคลื่นดำ', 'pirate', { x: 20, y: 76 }, 9, { gold: 200, cultivation: 130, jade: 6 }),
  camp('city-desert', 'city-desert', 'รังแมงป่องทรายแดง', 'scorpion', { x: 20, y: 76 }, 9, { gold: 200, cultivation: 130, jade: 6 }),
  camp('city-snow', 'city-snow', 'ถ้ำหมาป่าน้ำแข็ง', 'wolf', { x: 20, y: 76 }, 9, { gold: 240, cultivation: 160, jade: 8 }),
  camp('plains', 'plains', 'ค่ายโจรทุ่งกว้าง', 'bandit', { x: 200, y: 60 }, 14, { gold: 150, cultivation: 100, jade: 5 }, 7),
];

export const SMALL_DUNGEON_MONSTERS: readonly MonsterDef[] = SMALL_DUNGEONS.flatMap((c) => {
  const t = THEMES[c.theme]!;
  return [
    mk(c.minion, t.names[0], t, { hp: 1, atk: 1, scale: 1 }, 0, t.sprite),
    mk(c.sub, t.names[1], t, SUB, 1, t.subSprite),
    mk(c.boss, t.names[2], t, BOSS, 2, t.bossSprite),
  ];
});

export const smallDungeonsIn = (zone: string): readonly SmallDungeon[] => SMALL_DUNGEONS.filter((c) => c.zone === zone);
